import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  다국어 설정 (웹)
 * ════════════════════════════════════════════════════════════════════════════════
 *  지원언어 : 한국어 / 영어 / 일본어 / 중국어 / 인도네시아어
 *  번역파일 : public/locales/{언어}/translation.json
 *
 *  [supportedLngs 와 load 가 왜 필요한가]
 *   브라우저가 알려주는 언어코드에는 지역이 붙어 있다. (ko-KR, en-US, ja-JP, zh-CN, id-ID)
 *   이 값을 그대로 쓰면 아래 두 가지 문제가 생긴다.
 *
 *   1) 없는 번역파일을 요청한다
 *      /locales/ko-KR/translation.json 같은 폴더는 없다.
 *      그런데 이 프로젝트는 SPA 라서, 없는 정적경로를 요청하면
 *      개발서버와 nginx 모두 404 가 아니라 index.html 을 200 으로 돌려준다.
 *      i18next 는 그 HTML 을 번역파일로 알고 파싱하다 실패한다.
 *
 *   2) 화면 곳곳의 언어분기가 어긋난다
 *      여러 화면이 i18n.language 를 'en' / 'ja' / 'zh' / 'id' 와 직접 비교하고
 *      나머지를 한국어로 처리한다. (UserHome, SideQuickMenu, UsageGuide, FAQ 등)
 *      'en-US' 는 'en' 과 같지 않으므로, 영어권 사용자가 한국어 분기로 떨어진다.
 *
 *   load: 'languageOnly' 가 '번역파일을 어느 코드로 받을지' 를 정리하고(ko-KR -> ko),
 *   supportedLngs 가 목록에 없는 언어를 아예 요청하지 않게 막는다.
 *
 *   [중요] 이 둘만으로는 2)번이 해결되지 않는다.
 *    실제로 돌려보니 load:'languageOnly' 는 i18n.languages(로딩 목록)만 바꾸고
 *    i18n.language 는 'ko-KR' 그대로 남는다.
 *      lng='en-US' -> language='en-US', languages=['en','ko']
 *    화면들이 보는 값은 i18n.language 이므로 여전히 어긋난다.
 *    그래서 convertDetectedLanguage 로 감지 단계에서 지역코드를 떼어낸다.
 *    (i18next-browser-languagedetector 7.1 이상 지원 — 현재 7.2.0)
 *
 *  [detection 을 맞춰둔 이유]
 *   사용자가 고른 언어는 화면 여러 곳에서 localStorage 의 'lang' 키에 저장한다.
 *   (UserHeaderRenew / HeaderMain / UserLoginPg / StoreLinkPopup / MFooterSection 등)
 *   그런데 감지기는 기본적으로 'i18nextLng' 라는 다른 키를 본다.
 *   서로 다른 값을 보면 처음 뜰 때 엉뚱한 언어로 한 번 그려졌다 바뀐다.
 *   감지기도 같은 'lang' 키를 보도록 맞춰 한 곳만 바라보게 한다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 실제 public/locales 아래에 폴더가 있는 언어만 적는다 */
export const SUPPORTED_LANGUAGES = ['ko', 'en', 'ja', 'zh', 'id'];

/*
 * 관리자 화면(/btcorpad)은 한국어 고정이다.
 * ※ 이 값은 모듈이 처음 읽힐 때 한 번만 정해진다.
 *   관리자 화면과 사용자 화면은 서로 이동하지 않으므로 문제가 없다.
 */
const isAdminPg = window.location.pathname.startsWith('/btcorpad');

i18n
    .use(HttpBackend) // 서버에서 언어파일 로드
    .use(LanguageDetector) // 브라우저 언어감지
    .use(initReactI18next) // react-i18next와 i18next를 연결
    .init({
        backend: {
            loadPath: isAdminPg ? '/locales/ko/translation.json' : '/locales/{{lng}}/{{ns}}.json',
        },
        fallbackLng: 'ko',          // 기본언어
        supportedLngs: SUPPORTED_LANGUAGES,
        load: 'languageOnly',       // ko-KR -> ko 로 지역코드를 떼어낸다
        nonExplicitSupportedLngs: true,
        detection: {
            // 화면들이 저장하는 키와 동일하게 맞춘다
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: 'lang',
            caches: ['localStorage'],
            /*
             * 감지 단계에서 지역코드를 떼어낸다. (ko-KR -> ko, en-US -> en)
             * 이래야 i18n.language 자체가 'ko'/'en'/'ja'/'zh'/'id' 다섯 중 하나가 되어
             * 화면들의 i18n.language === 'en' 같은 분기가 정상 동작한다.
             * 목록에 없는 언어(fr 등)는 supportedLngs 와 fallbackLng 가 한국어로 보낸다.
             */
            convertDetectedLanguage: (lng) => String(lng || '').split('-')[0].toLowerCase(),
        },
        debug: false,
        interpolation: {
            escapeValue: false, // React에서는 기본적으로 안전함
        },
    });

export default i18n;
